<template>
  <div
    :class="[
      'absolute top-4 left-4 z-10 bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-200 overflow-hidden',
      collapsed ? 'w-10' : 'w-72',
    ]"
    style="max-height: calc(100% - 2rem)"
  >
    <!-- Collapsed state -->
    <button
      v-if="collapsed"
      class="w-full h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 bg-transparent border-0 cursor-pointer"
      @click="collapsed = false"
    >
      <font-awesome-icon :icon="['fas', 'chevron-right']" class="text-xs" />
    </button>

    <div v-if="!collapsed" class="p-4 overflow-y-auto" style="max-height: calc(100vh - 12rem)">
      <!-- Header -->
      <div class="flex items-center justify-between mb-3">
        <span class="text-sm font-semibold text-gray-700">Filters</span>
        <div class="flex items-center gap-2">
          <button
            class="text-xs text-primary hover:underline bg-transparent border-0 cursor-pointer"
            @click="clearAll"
          >
            Clear All
          </button>
          <button
            class="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 bg-transparent border-0 cursor-pointer"
            @click="collapsed = true"
          >
            <font-awesome-icon :icon="['fas', 'chevron-left']" class="text-xs" />
          </button>
        </div>
      </div>

      <!-- Marker count -->
      <div class="text-xs text-gray-500 mb-4">
        {{ visibleCount }} of {{ totalCount }} practices
      </div>

      <!-- Location Type toggle -->
      <div class="mb-4">
        <label class="block text-xs font-medium text-gray-600 mb-1">Location Type</label>
        <div class="flex gap-1">
          <button
            v-for="opt in [
              { value: 'Primary', label: 'Primary only' },
              { value: 'All', label: 'All' },
            ]"
            :key="opt.value"
            :class="[
              'px-2.5 py-1 text-xs rounded border cursor-pointer transition-colors',
              filters.location_type === opt.value
                ? 'bg-gray-800 text-white border-gray-800'
                : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400',
            ]"
            @click="setFilter('location_type', opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
        <p v-if="filters.location_type === 'All'" class="text-[10px] text-gray-400 mt-1">
          Showing primary + additional locations
        </p>
      </div>

      <!-- Multi-select filters -->
      <div v-for="f in multiSelectFilters" :key="f.field" class="mb-4">
        <label class="block text-xs font-medium text-gray-600 mb-1">{{ f.label }}</label>
        <div v-click-outside="() => (f._open = false)" class="relative">
          <button
            :class="[
              'w-full flex items-center justify-between px-2.5 py-1.5 text-xs border border-gray-300 rounded',
              getOptions(f.field).length
                ? 'bg-white cursor-pointer hover:border-gray-400'
                : 'bg-gray-50 cursor-not-allowed text-gray-400',
            ]"
            :disabled="!getOptions(f.field).length"
            @click="f._open = !f._open"
          >
            <span class="truncate" :class="getOptions(f.field).length ? 'text-gray-700' : 'text-gray-400'">
              {{ getOptions(f.field).length ? filterSummary(f.field) : 'No values available' }}
            </span>
            <font-awesome-icon :icon="['fas', 'chevron-down']" class="text-gray-400 text-[10px] ml-1 shrink-0" />
          </button>
          <div
            v-if="f._open && getOptions(f.field).length"
            class="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-30 max-h-48 overflow-y-auto"
          >
            <label
              v-for="option in getOptions(f.field)"
              :key="option == null ? '__null__' : option"
              class="flex items-center gap-2 px-2.5 py-1.5 text-xs hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                :checked="filters[f.field].includes(option)"
                class="rounded border-gray-300"
                @change="toggleOption(f.field, option)"
              />
              <span class="truncate" :class="{ italic: option == null }">{{ optionLabel(f.field, option) }}</span>
            </label>
          </div>
        </div>
        <!-- Selected chips -->
        <div v-if="filters[f.field].length" class="flex flex-wrap gap-1 mt-1">
          <span
            v-for="val in filters[f.field]"
            :key="val == null ? '__null__' : val"
            class="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-700 rounded"
          >
            {{ optionLabel(f.field, val) }}
            <button
              class="text-gray-400 hover:text-gray-600 bg-transparent border-0 cursor-pointer p-0 leading-none"
              @click="toggleOption(f.field, val)"
            >
              &times;
            </button>
          </span>
        </div>
      </div>

      <!-- Engagement Score -->
      <div class="mb-4">
        <label class="block text-xs font-medium text-gray-600 mb-1">Engagement Score</label>
        <div class="flex gap-1">
          <button
            v-for="n in 5"
            :key="n"
            :class="[
              'w-8 h-8 rounded text-xs font-medium cursor-pointer transition-colors border',
              filters.engagement_score.includes(n)
                ? 'bg-gray-800 text-white border-gray-800'
                : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400',
            ]"
            :title="n === 5 ? '5.0+' : `${n}.0–${n}.9`"
            @click="toggleEngagement(n)"
          >
            {{ n }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { labelFor } from './networkMapColors';

const MULTI_SELECT_FIELDS = [
  { label: 'Market', field: 'market' },
  { label: 'Sub-network', field: 'sub_network' },
  { label: 'Practice Tier', field: 'practice_tier' },
  { label: 'PL Owner', field: 'pl_owner_name' },
  { label: 'Contract', field: 'contracts' },
  { label: 'Implementation Status', field: 'implementation_status' },
  { label: 'Participation Status', field: 'participation_status' },
];

export default {
  name: 'NetworkMapFilters',
  components: { FontAwesomeIcon },
  directives: {
    'click-outside': {
      mounted(el, binding) {
        el._clickOutside = (e) => {
          if (!el.contains(e.target)) binding.value();
        };
        document.addEventListener('click', el._clickOutside);
      },
      unmounted(el) {
        document.removeEventListener('click', el._clickOutside);
      },
    },
  },
  props: {
    markers: { type: Array, default: () => [] },
    visibleCount: { type: Number, default: 0 },
    totalCount: { type: Number, default: 0 },
  },
  emits: ['update:filters'],
  data() {
    return {
      collapsed: false,
      multiSelectFilters: MULTI_SELECT_FIELDS.map((f) => ({ ...f, _open: false })),
      filters: this.emptyFilters(),
    };
  },
  watch: {
    filters: {
      deep: true,
      handler(val) {
        this.$emit('update:filters', val);
      },
    },
  },
  mounted() {
    // Emit initial filter state so defaults (e.g. "Primary only") apply on load.
    this.$emit('update:filters', this.filters);
  },
  methods: {
    emptyFilters() {
      return {
        location_type: 'Primary',
        sub_network: [],
        practice_tier: [],
        pl_owner_name: [],
        contracts: [],
        implementation_status: [],
        participation_status: [],
        engagement_score: [],
        market: [],
      };
    },
    clearAll() {
      this.filters = this.emptyFilters();
    },
    setFilter(field, value) {
      this.filters[field] = value;
    },
    toggleOption(field, value) {
      const arr = this.filters[field];
      const idx = arr.indexOf(value);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(value);
    },
    toggleEngagement(n) {
      const arr = this.filters.engagement_score;
      const idx = arr.indexOf(n);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(n);
    },
    getOptions(field) {
      if (field === 'contracts') {
        // A practice with no contracts contributes the null option ("No contract").
        let hasNull = false;
        const all = [];
        this.markers.forEach((m) => {
          if (m.contracts && m.contracts.length) all.push(...m.contracts);
          else hasNull = true;
        });
        const opts = [...new Set(all)].sort((a, b) => a.localeCompare(b));
        if (hasNull) opts.push(null);
        return opts;
      }
      let hasNull = false;
      const vals = new Set();
      this.markers.forEach((m) => {
        const v = m[field];
        if (v == null || v === '') hasNull = true;
        else vals.add(v);
      });
      const opts = [...vals].sort((a, b) => String(a).localeCompare(String(b)));
      if (hasNull) opts.push(null);
      return opts;
    },
    optionLabel(field, value) {
      return labelFor(field, value);
    },
    filterSummary(field) {
      const selected = this.filters[field];
      if (!selected.length) return 'All';
      if (selected.length === 1) return this.optionLabel(field, selected[0]);
      return `${selected.length} selected`;
    },
  },
};
</script>
